import React from "react";
import { Phone, MessageCircle, MapPin, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { PHONE_E164, PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
function RecursiveFooterLinks({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-3" : "pr-4 mt-2 space-y-2 border-r border-border/40"}>
      {items.map((item, i) => <li key={item.id || i} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground hover:text-primary transition-colors inline-block">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterLinks items={item.children} level={level + 1} />}
        </li>)}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        <div className="h-4 w-20 bg-muted/20 animate-pulse rounded" />
        <div className="h-4 w-24 bg-muted/20 animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-4 w-28 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackLinks: MenuItem[] = [{
      id: "1",
      label: "الرئيسية",
      href: "/"
    }, {
      id: "2",
      label: "خدماتنا",
      href: "/خدماتنا"
    }, {
      id: "3",
      label: "المدونة",
      href: "/المدونة"
    }, {
      id: "4",
      label: "تواصل معنا",
      href: "/تواصل-معنا"
    }];
    return <RecursiveFooterLinks items={fallbackLinks} />;
  }
  return <RecursiveFooterLinks items={menuItems} />;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="الفوتر" data-nav="dark" dir="rtl" className="dark bg-background text-foreground border-t border-border pt-16 pb-12">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-12">
          {/* Column 1: Brand & Overview (4 cols) */}
          <div className="lg:col-span-4 flex flex-col items-start gap-4">
            <Link to="/">
              <WvcLogo className="h-14 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              المتخصصون الأوائل في صيانة وتنظيف الأفران المنزلية داخل الرياض بخدمة منزلية سريعة وموثوقة.
            </p>
          </div>

          {/* Column 2: Fast Navigation Links (3 cols) */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            <h3 className="text-base font-semibold text-foreground tracking-tight">
              روابط سريعة
            </h3>
            <MenuProvider menu_id="19" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Column 3: Service Coverage (2 cols) */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <h3 className="text-base font-semibold text-foreground tracking-tight">
              نطاق الخدمة
            </h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <MapPin className="size-4 text-primary shrink-0 mt-1" />
                <span>منطقة الخدمة: مدينة الرياض بالكامل</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="size-4 text-primary shrink-0 mt-1" />
                <span>طوال أيام الأسبوع: 8 ص - 10 م</span>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact Information (3 cols) */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            <h3 className="text-base font-semibold text-foreground tracking-tight">
              معلومات التواصل
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={PHONE_TEL_HREF} className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group">
                  <span className="flex items-center justify-center size-8 rounded bg-primary/10 border border-primary/25 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Phone className="size-4" />
                  </span>
                  <span dir="ltr" className="font-mono text-sm">{PHONE_E164}</span>
                </a>
              </li>
              <li>
                <Link to={WHATSAPP_HREF} newTab className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group">
                  <span className="flex items-center justify-center size-8 rounded bg-primary/10 border border-primary/25 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <MessageCircle className="size-4" />
                  </span>
                  <span>واتساب: <span dir="ltr" className="font-mono">{PHONE_LOCAL}</span></span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-border/60" />

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>
            © {currentYear} لمسة احتراف. جميع الحقوق محفوظة.
          </p>
          <p>
            خدمة منزلية معتمدة للأفران في الرياض
          </p>
        </div>
      </div>
    </section>;
}